import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '@/App';
import { getCourseRoom, updateCourse } from '@/lib/api';
import { flatClasswork, buildClasswork, progressPercent, tabCounts, tabsForRole, totalDuration } from '@/lib/courseRoom';
import { normalizeRichText } from '@/lib/richText';
import ClassworkPane from './ClassworkPane';
import ClassworkDetail from './ClassworkDetail';
import ClassworkDialog from './ClassworkDialog';
import LiveTab from './LiveTab';
import { GradebookTab, RosterTab } from './GradebookTab';
import CourseResources from '@/components/course/CourseResources';
import CourseDesignDialog from '@/components/course/CourseDesignDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Check, Clock, Copy, Eye, Loader2, Lock, Pencil, PencilLine, Settings, Users, X } from 'lucide-react';

/**
 * The one course workspace.
 *
 * Students, instructors and admins open the same page against the same
 * endpoint. The server answers with `role`, and that single value decides
 * whether the page renders editing controls — everything it is willing to hand
 * over has already been filtered server-side, so the browser is never the thing
 * enforcing it.
 *
 * The signed-in role is used for one thing only: choosing which URL prefix this
 * room's own links point at, since each role's routes are guarded separately.
 * It never decides what may be edited.
 */
export default function CourseRoom() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { role: userRole } = useAppContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [designOpen, setDesignOpen] = useState(false);
  const [classworkDialog, setClassworkDialog] = useState(null);
  const [preview, setPreview] = useState(false);
  const [editingOverview, setEditingOverview] = useState(false);
  const [overviewDraft, setOverviewDraft] = useState('');
  const [savingOverview, setSavingOverview] = useState(false);

  const load = useCallback(async () => {
    try {
      setError('');
      const { data: room } = await getCourseRoom(courseId);
      setData(room);
      setSelectedId((current) => {
        const items = flatClasswork(buildClasswork({
          lessons: room.lessons, assignments: room.assignments,
          exams: room.exams, modules: room.modules,
        }));
        if (current && items.some((i) => i.id === current)) return current;
        return items[0]?.id || '';
      });
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 404 || status === 403
          ? 'You do not have access to this course.'
          : err?.response?.data?.detail || 'Could not open this course.',
      );
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  const canEdit = data?.role === 'editor';
  const isAdmin = userRole === 'admin';
  /**
   * Preview-as-student.
   *
   * The room is the same page for both audiences, so seeing it as a learner
   * sees it is a matter of rendering the viewer's half of it — no second route
   * and no impersonation. It is strictly a way of looking: every student action
   * is inert while previewing, so it can never record progress, create a
   * submission, consume an attempt, or show up in anybody's record.
   */
  const previewing = canEdit && preview;
  const isEditor = canEdit && !preview;
  // Which set of routes this room links into. An admin sent to /instructor/...
  // would be bounced by that route's guard.
  const basePath = isAdmin ? '/admin' : (canEdit ? '/instructor' : '/student');
  const classwork = useMemo(() => {
    if (!data) return [];
    const keep = (rows) => (data.role === 'editor' && preview
      ? (rows || []).filter((row) => row.status === 'published')
      : (rows || []));
    return flatClasswork(buildClasswork({
      lessons: keep(data.lessons), assignments: keep(data.assignments),
      exams: keep(data.exams), modules: data.modules,
    }));
  }, [data, preview]);
  const selected = useMemo(
    () => classwork.find((item) => item.id === selectedId) || null,
    [classwork, selectedId],
  );

  const saveOverview = async () => {
    try {
      setSavingOverview(true);
      await updateCourse(courseId, { overview_html: overviewDraft });
      toast.success('Overview saved');
      setEditingOverview(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the overview');
    } finally {
      setSavingOverview(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Loader2 className="size-7 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (error || !data?.course) {
    return (
      <div className="py-16 text-center">
        <Lock className="mx-auto mb-3 size-12 text-[#062B1A]/15" />
        <h2 className="text-lg font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          {error || 'Course not found'}
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-[#062B1A]/50">
          Course registration is handled by the registrar. Contact the college if you believe this is
          an error.
        </p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>Go back</Button>
      </div>
    );
  }

  const { course } = data;
  // A draft is invisible to a student, so the preview must not show one either
  // — otherwise it would answer a different question than the one being asked.
  const published = (rows) => (rows || []).filter((row) => row.status === 'published');
  const visibleLessons = previewing ? published(data.lessons) : (data.lessons || []);
  const visibleAssignments = previewing ? published(data.assignments) : (data.assignments || []);
  const visibleExams = previewing ? published(data.exams) : (data.exams || []);
  const completed = previewing ? [] : (data.completed_lessons || []);
  // The server computes completion across the whole classwork stream. The local
  // fallback only covers materials, so it is a shape guard for an older payload,
  // not a second opinion.
  const completion = (previewing ? null : data.completion) || {
    content_done: completed.filter((id) => visibleLessons.some((l) => l.id === id)).length,
    content_total: visibleLessons.length,
    assessments_done: 0,
    assessments_total: visibleAssignments.length + visibleExams.length,
  };
  const progress = previewing ? 0 : (isEditor ? 0 : (data.progress ?? progressPercent(completed, data.lessons)));
  const tabs = tabsForRole(previewing ? 'viewer' : data.role);
  const counts = tabCounts(data);
  const overviewHtml = course.overview_html || '';
  const legacySyllabus = course.syllabus || course.description || '';

  return (
    <div className="animate-fade-in space-y-6">
      {previewing && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-3"
          data-testid="preview-banner"
        >
          <Eye className="size-4 text-[#B8941F]" />
          <p className="flex-1 text-sm text-[#062B1A]/80">
            <span className="font-semibold">Previewing as a student.</span>{' '}
            This is what an enrolled learner sees. Nothing here is recorded — marking work
            complete, handing in and sitting a quiz are all switched off, and drafts are hidden.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" className="text-[#062B1A]/60" onClick={() => navigate(-1)}>
          <ArrowLeft className="size-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            {course.title}
          </h2>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            {/* Which cohort this run belongs to, by name — never its id. */}
            {data.intake && (
              <Badge variant="outline" className="border-[#062B1A]/20 text-[10px] text-[#062B1A]/70">
                {data.intake.display_name}
              </Badge>
            )}
            {course.course_code && (
              <Badge variant="outline" className="border-[#062B1A]/20 text-[10px] text-[#062B1A]/70">
                {course.course_code}
              </Badge>
            )}
            <Badge variant="outline" className="border-[#D4AF37]/30 text-[10px] text-[#D4AF37]">
              {course.category}
            </Badge>
            <span className="flex items-center gap-1 text-xs text-[#062B1A]/50">
              <Users className="size-3" />{course.enrollment_count || 0} students
            </span>
            <span className="flex items-center gap-1 text-xs text-[#062B1A]/50">
              <Clock className="size-3" />{totalDuration(data.lessons)} min
            </span>
          </div>
        </div>
        {isEditor ? (
          <div className="flex items-center gap-2">
            <Badge className="border-0 bg-[#062B1A] text-white">{course.status || 'draft'}</Badge>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                data-testid="course-settings"
                onClick={() => navigate(`/admin/courses/${courseId}/settings`)}
              >
                <Settings className="mr-2 size-4" />Course settings
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              data-testid="preview-toggle"
              onClick={() => setPreview(true)}
            >
              <Eye className="mr-2 size-4" />Preview as student
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDesignOpen(true)}>
              <Copy className="mr-2 size-4" />Reuse a design
            </Button>
          </div>
        ) : previewing ? (
          <Button
            size="sm"
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            data-testid="preview-exit"
            onClick={() => setPreview(false)}
          >
            <PencilLine className="mr-2 size-4" />Back to editing
          </Button>
        ) : (
          <Badge className="border-0 bg-green-100 text-green-700">Enrolled</Badge>
        )}
      </div>

      {/* Progress — the student's own, or the class's for a teacher.
          A learner gets two meters, because a course that counted only its
          readings reported 100% complete with every quiz and assignment
          untouched. Reading and marked work are different kinds of done, so
          they are shown as two figures rather than averaged into one. */}
      {isEditor ? (
        <div className="flex items-center gap-4 rounded-xl border border-[rgba(6,43,26,0.08)] bg-white p-4">
          <div className="flex-1">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-medium text-[#062B1A]/60">Class progress</span>
              <span className="text-xs font-semibold text-[#D4AF37]">
                {data.summary?.pending_submissions || 0} awaiting marking
              </span>
            </div>
            <Progress
              value={Math.round(((data.students || []).reduce((s, st) => s + (st.course_progress || 0), 0)) / Math.max((data.students || []).length, 1))}
              className="h-2 bg-[#062B1A]/10 [&>div]:bg-[#D4AF37]"
            />
          </div>
          <span className="flex-shrink-0 text-xs text-[#062B1A]/50">
            {(data.students || []).length} student{(data.students || []).length === 1 ? '' : 's'}
          </span>
        </div>
      ) : (
        <div className="rounded-xl border border-[rgba(6,43,26,0.08)] bg-white p-4">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-xs font-medium text-[#062B1A]/60">Course Progress</span>
            <span className="text-xs font-semibold text-[#D4AF37]" data-testid="course-progress-overall">
              {progress}% overall
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[11px] font-medium text-[#062B1A]/55">Content read</span>
                <span className="text-[11px] text-[#062B1A]/50" data-testid="progress-content">
                  {completion.content_done}/{completion.content_total} done
                </span>
              </div>
              <Progress
                value={completion.content_total
                  ? Math.round((completion.content_done / completion.content_total) * 100) : 0}
                className="h-2 bg-[#062B1A]/10 [&>div]:bg-[#D4AF37]"
              />
            </div>
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[11px] font-medium text-[#062B1A]/55">Assessments done</span>
                <span className="text-[11px] text-[#062B1A]/50" data-testid="progress-assessments">
                  {completion.assessments_done}/{completion.assessments_total} done
                </span>
              </div>
              <Progress
                value={completion.assessments_total
                  ? Math.round((completion.assessments_done / completion.assessments_total) * 100) : 0}
                className="h-2 bg-[#062B1A]/10 [&>div]:bg-[#0F9D58]"
              />
            </div>
          </div>
          {completion.assessments_total > completion.assessments_done && (
            <p className="mt-3 text-[11px] text-[#062B1A]/45">
              {completion.assessments_total - completion.assessments_done} piece
              {completion.assessments_total - completion.assessments_done === 1 ? '' : 's'} of marked work still
              to hand in.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <ClassworkPane
          courseId={courseId}
          lessons={visibleLessons}
          assignments={visibleAssignments}
          exams={visibleExams}
          modules={data.modules || []}
          selectedId={selectedId}
          completedLessons={completed}
          isEditor={isEditor}
          onSelect={(item) => setSelectedId(item.id)}
          onCreate={(kind) => setClassworkDialog({ kind })}
          onEdit={(item) => setClassworkDialog({ kind: item.kind, item })}
          onChanged={load}
        />

        <div className="space-y-6 lg:col-span-8">
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <ClassworkDetail
              courseId={courseId}
              basePath={basePath}
              preview={previewing}
              item={selected}
              /* The teaching payload holds every student's submissions. A preview
                 is nobody's attempt, so it starts from none — showing an enrolled
                 learner's mark here would be both wrong and a disclosure. */
              submissions={previewing ? [] : (data.submissions || [])}
              students={data.students || []}
              completedLessons={completed}
              isEditor={isEditor}
              onEdit={(item) => setClassworkDialog({ kind: item.kind, item })}
              onChanged={load}
            />
          </Card>

          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="p-6">
              <Tabs defaultValue="overview">
                <TabsList className="mb-4 h-auto flex-wrap justify-start bg-[#F7F8FA]">
                  {tabs.map((tab) => (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className="text-xs data-[state=active]:bg-[#062B1A] data-[state=active]:text-white"
                    >
                      {tab.label}{counts[tab.id] ? ` (${counts[tab.id]})` : ''}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="overview">
                  {editingOverview ? (
                    <div className="space-y-3">
                      <RichTextEditor
                        value={overviewDraft}
                        onChange={({ html }) => setOverviewDraft(html)}
                      />
                      <div className="flex gap-2">
                        <Button className="bg-[#062B1A] text-white hover:bg-[#041B10]" disabled={savingOverview} onClick={saveOverview}>
                          {savingOverview ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Check className="mr-2 size-4" />}
                          Save overview
                        </Button>
                        <Button variant="outline" onClick={() => setEditingOverview(false)}>
                          <X className="mr-2 size-4" />Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {isEditor && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setOverviewDraft(overviewHtml); setEditingOverview(true); }}
                          data-testid="edit-overview"
                        >
                          <Pencil className="mr-2 size-3.5" />
                          {overviewHtml ? 'Edit overview' : 'Write the course overview'}
                        </Button>
                      )}
                      {overviewHtml ? (
                        // Sanitized server-side on every write.
                        <div
                          className="prose prose-sm max-w-none text-[#062B1A]/80"
                          dangerouslySetInnerHTML={{ __html: normalizeRichText(overviewHtml) }}
                        />
                      ) : legacySyllabus ? (
                        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-[#062B1A]/80">
                          {legacySyllabus}
                        </div>
                      ) : (
                        <p className="py-8 text-center text-sm text-[#062B1A]/45">
                          {isEditor
                            ? 'No overview yet. Write one so your class knows what this course covers.'
                            : 'Your instructor has not written a course overview yet.'}
                        </p>
                      )}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="materials">
                  <CourseResources
                    courseId={courseId}
                    editable={isEditor}
                    emptyLabel={isEditor
                      ? 'Nothing shared yet. Upload a file or add a link.'
                      : 'Your instructor has not shared any course materials yet.'}
                  />
                </TabsContent>

                <TabsContent value="live">
                  <LiveTab
                    courseId={courseId}
                    sessions={data.sessions || []}
                    isEditor={isEditor}
                    roomBasePath={basePath}
                    onChanged={load}
                  />
                </TabsContent>

                {isEditor && (
                  <>
                    <TabsContent value="students">
                      <RosterTab students={data.students || []} />
                    </TabsContent>
                    <TabsContent value="gradebook">
                      <GradebookTab
                        courseId={courseId}
                        students={data.students || []}
                        grades={data.grades || []}
                        attendance={data.attendance || []}
                        assignments={data.assignments || []}
                        exams={data.exams || []}
                        submissions={data.submissions || []}
                        modules={data.modules || []}
                        onChanged={load}
                      />
                    </TabsContent>
                  </>
                )}
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>

      {isEditor && (
        <>
          <ClassworkDialog
            open={!!classworkDialog}
            onOpenChange={(open) => !open && setClassworkDialog(null)}
            courseId={courseId}
            kind={classworkDialog?.kind}
            item={classworkDialog?.item}
            modules={data.modules || []}
            onSaved={load}
          />
          <CourseDesignDialog
            open={designOpen}
            onOpenChange={setDesignOpen}
            courseId={courseId}
            courseTitle={course.title}
            onApplied={load}
          />
        </>
      )}
    </div>
  );
}
